import GenericSettings from "../GenericSettings/GenericSettings";
import { cqAPI } from "../../lib/Auth";
import React, { useEffect, useState } from "react";
import {
  Button,
  Text,
  Flex,
  Box,
  SimpleGrid,
  MenuButton,
  IconButton,
  Menu,
  MenuList,
  MenuItem,
  useColorModeValue,
  ChakraProvider,
  Spinner,
} from "@chakra-ui/react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import ReactDOM from "react-dom/client";

const getParam = (p) => {
  const newSearchParams = new URLSearchParams(window.location.search);
  return newSearchParams.get(p);
};

const convertToGeneric = (template) => {
  return template?.map((section) => {
    return {
      title: section.title,
      description: section.description || "",
      inputs: section.questions.map((q) => ({
        type:
          q.type === "t"
            ? "input"
            : q.type === "yn"
            ? "switch"
            : q.type === "multiple"
            ? "select"
            : q.type === "ynwt"
            ? "switchwt"
            : q.type,
        options: q?.options?.length
          ? q?.options.map((option) => {
              if (typeof option === "string") {
                return { label: option, value: option };
              }
              return option;
            })
          : [],
        id: q.id,
        hint: q.hint,
        name: q.name || q.text,
        required: q.required,
      })),
    };
  });
};

const PatientHistory = ({ view, setView }) => {
  const [templates, setTemplates] = useState(null);
  const [targetTemplate, setTargetTemplate] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingMessage, setDownloadingMessage] = useState("")

  // Color mode values (hooks must be called unconditionally)
  const overlayBg = useColorModeValue("whiteAlpha.700", "blackAlpha.700");
  const cardBg = useColorModeValue("white", "gray.800");

  const bid = getParam("bid");
  const uid = getParam("_uid");
  const and_bid_uid = bid && uid ? `&bid=${bid}&_uid=${uid}` : "";
  const q_bid_uid = bid && uid ? `?bid=${bid}&_uid=${uid}` : "";

  const fetchHistory = async () => {
    const response = await cqAPI.get(`/pp/history${q_bid_uid}`);
    setTemplates(response.data?.templates || []);
  };

  // Utility to build a print container and render a print-mode GenericSettings into it.
  const renderOffscreenForTemplate = (template, onReady) => {
    // create offscreen container
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-99999px";
    container.style.top = "0";
    // Render at A4 width in CSS pixels (~8.27in * 96 = ~794px)
    container.style.width = "794px";
    container.style.background = "#ffffff";
    container.style.overflow = "visible";
    container.style.padding = "32px";
    container.className = "print-root";
    document.body.appendChild(container);

    // neutralize transforms/overflow for the print subtree only
    const style = document.createElement("style");
    style.textContent = `
      .print-root, .print-root * {
        transform: none !important;
        -webkit-transform: none !important;
        filter: none !important;
        overflow: visible !important;
      }
    `;
    container.appendChild(style);

    const root = ReactDOM.createRoot(container);

    const Capture = async () => {
      try {
        // Let the browser paint the spinner, then wait for fonts/layout
        const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));
        await nextFrame();
        await nextFrame();
        if (document.fonts?.ready) {
          try {
            await document.fonts.ready;
          } catch {}
        }
        onReady({ container, root });
      } catch (e) {
        // propagate
        onReady({ container, root, error: e });
      }
    };

    root.render(
      <ChakraProvider>
        <Box p={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={2} textAlign="center">
            {template.name}
          </Text>

          <Flex direction="column" align="stretch" justify="flex-start" gap={6}>
            <GenericSettings
              description={template.description}
              configuration={convertToGeneric(template.data)}
              endpoint={`/pp/read-history/${template._id}`}
              bid_uid={and_bid_uid}
              long_query={true}
              supportBatching={true}
              viewOnly={true}
              printMode={true}
              // Ensure this runs AFTER data is fetched by GenericSettings
              onLoad={() => setTimeout(Capture, 0)}
            />
          </Flex>
        </Box>
      </ChakraProvider>
    );
  };

  // -------------- SMALLER PDF + UPLOAD (NO DOWNLOAD) --------------
  const sendPDF = async (template) => {
    setDownloadingMessage("Saving PDF")
    setIsDownloading(true);
    try {
      renderOffscreenForTemplate(template, async ({ container, root, error }) => {
        if (error) {
          try { root.unmount(); } catch {}
          try { document.body.removeChild(container); } catch {}
          setIsDownloading(false);
          console.error("Render failed before capture", error);
          alert("Failed to generate PDF for upload.");
          return;
        }

        try {
          // Capture with slightly lower scale to keep text readable but file small
          const canvas = await html2canvas(container, {
            scale: 1.5,            // smaller than 2 → smaller output
            useCORS: true,
            backgroundColor: "#ffffff",
            scrollX: 0,
            scrollY: 0,
            windowWidth: container.scrollWidth,
            windowHeight: container.scrollHeight,
          });

          // Create paginated PDF by slicing the canvas per page (JPEG for compression)
          const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          // Calculate how tall (in px) each PDF page is at the captured canvas width
          const pageHeightPx = Math.floor((pageHeight / pageWidth) * canvas.width);

          let currentY = 0;
          let firstPage = true;

          while (currentY < canvas.height) {
            const sliceHeight = Math.min(pageHeightPx, canvas.height - currentY);

            // Slice the big canvas to just this page
            const pageCanvas = document.createElement("canvas");
            pageCanvas.width = canvas.width;
            pageCanvas.height = sliceHeight;
            const ctx = pageCanvas.getContext("2d");
            ctx.drawImage(
              canvas,
              0,
              currentY,
              canvas.width,
              sliceHeight,
              0,
              0,
              canvas.width,
              sliceHeight
            );

            // JPEG with compression for much smaller file size
            const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.65);

            // Add the slice as an image that fits the page width
            const pageImgHeightMm = (sliceHeight / canvas.width) * pageWidth;
            if (!firstPage) pdf.addPage();
            pdf.addImage(pageImgData, "JPEG", 0, 0, pageWidth, pageImgHeightMm, undefined, "FAST");

            firstPage = false;
            currentY += sliceHeight;
          }

          // Get base64 (no data: prefix) and upload
          const dataUri = pdf.output("datauristring"); // "data:application/pdf;...;base64,XXXX"
          const base64 = dataUri.split(",")[1] || "";

          await cqAPI.put(`/pp/history/${template._id}`, { file: base64 });

          // Optional: toast/alert could be added here if you want a confirmation
          // alert("PDF uploaded successfully.");
        } catch (err) {
          console.error("sendPDF failed", err);
          alert("Failed to upload PDF.");
        } finally {
          try { root.unmount(); } catch {}
          try { document.body.removeChild(container); } catch {}
          setIsDownloading(false);
        }
      });
    } catch (outerErr) {
      console.error("sendPDF outer error", outerErr);
      setIsDownloading(false);
      alert("Failed to generate PDF for upload.");
    }
  };

  // -------------- EXISTING DOWNLOAD (kept as-is) --------------
  const download = async (template) => {
    try {
      setDownloadingMessage("Preparing your PDF")
      setIsDownloading(true);
      renderOffscreenForTemplate(template, async ({ container, root, error }) => {
        if (error) {
          try { root.unmount(); } catch {}
          try { document.body.removeChild(container); } catch {}
          setIsDownloading(false);
          console.error("Render failed before capture", error);
          alert("Failed to generate PDF.");
          return;
        }

        try {
          // Capture full container
          const canvas = await html2canvas(container, {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
            scrollX: 0,
            scrollY: 0,
            windowWidth: container.scrollWidth,
            windowHeight: container.scrollHeight,
          });

          // NOTE: This path uses a single big image with negative-offset pagination.
          // (Heavier file size; kept intentionally for download quality.)
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          const imgProps = pdf.getImageProperties(imgData);
          const imgWidthMm = pageWidth;
          const imgHeightMm = (imgProps.height / imgProps.width) * imgWidthMm;

          let position = 0;
          let heightLeft = imgHeightMm;

          pdf.addImage(imgData, "PNG", 0, position, imgWidthMm, imgHeightMm);
          heightLeft -= pageHeight;
          position = -pageHeight;

          while (heightLeft > 0) {
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, imgWidthMm, imgHeightMm);
            heightLeft -= pageHeight;
            position -= pageHeight;
          }

          pdf.save(`${template.name || "history"}.pdf`);
        } finally {
          try { root.unmount(); } catch {}
          try { document.body.removeChild(container); } catch {}
          setIsDownloading(false);
        }
      });
    } catch (err) {
      console.error("Download failed", err);
      alert("Failed to generate PDF. Make sure html2canvas and jspdf are installed.");
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  return (
    <Flex
      direction="column"
      align="center"
      justify="flex-start"
      height="100vh"
      p={4}
      paddingTop={0}
      overflowY="auto"
      width="100%"
    >
      {!view && (
        <Box w="100%">
          <SimpleGrid columns={{ base: 1, md: 3, lg: 1 }}>
            {Array.isArray(templates) &&
              templates.map((tpl, idx) => (
                <Box
                  key={`to-complete-${tpl._id}`}
                  borderWidth="1px"
                  borderRadius="lg"
                  mb={4}
                  width="100%"
                  className="actionableItem"
                >
                  <Flex direction="column" p={4} justify="space-between" height="100%">
                    <Flex justify="space-between" align="right" textAlign="left">
                      <Box>
                        <Text fontSize="sm">
                          Title: <b>{tpl?.name}</b>
                        </Text>
                        <Text fontSize="sm">
                          {"Requested on"}:{" "}
                          <b>{moment(tpl.conversation_created).format("MMMM Do YYYY, h:mm:ss a")}</b>
                        </Text>
                      </Box>
                      <Flex alignItems="center" gap={4}>
                        {tpl.completed ? (
                          <Button
                            onClick={() => {
                              setTargetTemplate(tpl);
                              setView("view-completed");
                            }}
                          >
                            View Completed Questionnaire
                          </Button>
                        ) : (
                          <Button
                            onClick={() => {
                              setTargetTemplate(tpl);
                              setView("complete");
                            }}
                          >
                            Complete the Questionnaire
                          </Button>
                        )}
                        <Flex>
                          {tpl.completed && (<Menu direction={"flex-end"}>
                            <MenuButton
                              as={IconButton}
                              aria-label="Options"
                              icon={<FontAwesomeIcon icon={faEllipsisV} />}
                              variant="ellipsis"
                            />
                            <MenuList>
                              {tpl.enabled[bid] && (<MenuItem
                                id={`edit-${idx}`}
                                onClick={() => {
                                  setTargetTemplate(tpl);
                                  tpl.completed ? setView("update") : setView("complete");
                                }}
                              >
                                Update
                              </MenuItem>)}
                              <MenuItem id={`dnl-${idx}`} onClick={() => download(tpl)}>
                                Download as PDF
                              </MenuItem>
                            </MenuList>
                          </Menu>)}
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>
                </Box>
              ))}
          </SimpleGrid>
        </Box>
      )}

      {targetTemplate && view === "view-completed" && (
        <GenericSettings
          description={targetTemplate.description}
          configuration={convertToGeneric(targetTemplate.data)}
          endpoint={`/pp/read-history/${targetTemplate._id}`}
          bid_uid={and_bid_uid}
          long_query={true}
          supportBatching={true}
          viewOnly={true}
          onClose={() => setView("")}
        />
      )}
      {targetTemplate && view === "complete" && (
        <GenericSettings
          autoSave={true}
          saveButton={true}
          description={targetTemplate.description}
          configuration={convertToGeneric(targetTemplate.data)}
          endpoint={`/pp/history/${targetTemplate._id}`}
          bid_uid={and_bid_uid}
          long_query={true}
          supportBatching={true}
          onClose={(save) => {
            setView("");
            save && sendPDF(targetTemplate);
          }}
        />
      )}
      {targetTemplate && view === "update" && (
        <GenericSettings
          autoSave={true}
          saveButton={true}
          description={targetTemplate.description}
          configuration={convertToGeneric(targetTemplate.data)}
          endpoint={`/pp/history/${targetTemplate._id}`}
          bid_uid={and_bid_uid}
          long_query={true}
          supportBatching={true}
          onClose={(save) => {
            setView("");
            save && sendPDF(targetTemplate);
          }}
        />
      )}

      {/* Full-screen downloading/uploading overlay */}
      {isDownloading && (
        <Flex
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          align="center"
          justify="center"
          bg={overlayBg}
          zIndex={9999}
        >
          <Box
            p={6}
            bg={cardBg}
            rounded="xl"
            boxShadow="xl"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={3}
          >
            <Spinner size="xl" />
            <Text fontWeight="medium">{downloadingMessage}…</Text>
          </Box>
        </Flex>
      )}
    </Flex>
  );
};

export default PatientHistory;
